// material-ui
import {
  Container,
  Card,
  Typography,
  Box,
  CardHeader,
  CardContent,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  Divider,
  styled,
  alpha,
  InputBase,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Http } from 'api/http/http';
import { useDispatch } from 'react-redux';
import { logout } from 'store/reducers/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useCallback, useEffect, useMemo } from 'react';
import AdminEditStudentInfo from './AdminEditStudentInfo';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.9),
  boxShadow: theme.shadows[1],
  width: '25%',
  [theme.breakpoints.up('sm')]: {
    width: '25%'
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch'
      }
    }
  }
}));

const splitModulesData = (modulesData) => {
  let issuedModules = [];
  let applyingModules = [];

  if (modulesData && Array.isArray(modulesData)) {
    modulesData.forEach((module) => {
      if (module.status === 'APPROVED') {
        issuedModules = [...issuedModules, module.moduleName];
      } else if (module.status === 'PENDING') {
        applyingModules = [...applyingModules, module.moduleName];
      }
    });
  }

  return { issuedModules, applyingModules };
};

const splitScholarshipsData = (scholarshipsData) => {
  let issuedScholarships = [];
  let applyingScholarships = [];

  if (scholarshipsData && Array.isArray(scholarshipsData)) {
    scholarshipsData.forEach((scholarship) => {
      if (scholarship.status === '승인' || scholarship.status === '확정') {
        issuedScholarships = [...issuedScholarships, { id: scholarship.id, moduleName: scholarship.moduleName }];
      } else if (scholarship.status === '신청중' || scholarship.status === '심사중') {
        applyingScholarships = [...applyingScholarships, scholarship.moduleName];
      }
    });
  }

  return { issuedScholarships, applyingScholarships };
};

// 수강기록 전체를 (교과목 × 연결모듈) 단위 행으로 펼친다.
// 한 교과목이 여러 모듈에 연결되면 모듈마다 한 행씩 생성한다.
// 활용 불가한 교과목도 제외하지 않고, isAvailable 값으로 구분해 표시한다.
const getSubjectInfoList = (courseEnrollmentsData) => {
  if (!courseEnrollmentsData || !Array.isArray(courseEnrollmentsData)) return [];

  const levelMapping = {
    LOW: '초급',
    MIDDLE: '중급',
    HIGH: '고급'
  };

  const rows = [];

  courseEnrollmentsData.forEach((course) => {
    const semester =
      course.enrollmentYear != null && course.enrollmentSemester != null
        ? `${course.enrollmentYear}-${course.enrollmentSemester}`
        : '-';

    // 교과목 단위로 공통인 값
    const base = {
      courseId: course.courseId,
      semester, // 수강학기 (예: 2022-1)
      completionType: course.completionType || '-', // 이수구분
      standardCourseName: course.standardCourseName || '-', // 표준교과목명
      credit: course.credit != null ? course.credit : '-', // 학점(표준교과목)
      courseName: course.courseName, // 교과목명(개설교과목)
      isOpenedByTargetDepartment: course.openedByTargetDepartment, // 지능형로봇학과개설여부
      isAvailable: course.isAvailable // 활용가능여부 (미사용 + 인정기간 충족)
    };

    const moduleNames = Array.isArray(course.moduleName) ? course.moduleName : [];
    const levels = Array.isArray(course.level) ? course.level : [];

    if (moduleNames.length === 0) {
      rows.push({ ...base, courseLevel: '-', moduleName: '-' });
    } else {
      // 모듈마다 행을 나눈다 (과정명/모듈명만 달라짐)
      moduleNames.forEach((name, idx) => {
        const rawLevel = levels[idx] || levels[0] || '';
        rows.push({
          ...base,
          courseLevel: levelMapping[rawLevel] || rawLevel || '-', // 과정명 (초급/중급/고급)
          moduleName: name // 모듈명
        });
      });
    }
  });

  return rows;
};

const AdminStudentInfo = () => {
  const [studentNumber, setStudentNumber] = useState('');
  const [studentInfo, setStudentInfo] = useState({
    studentDetail: null,
    issuedModules: [],
    applyingModules: [],
    issuedScholarships: [],
    applyingScholarships: [],
    subjectInfoList: [],
    courseEnrollmentsRaw: [],
    selectionRequired: [],
    moduleStatusList: []
  });
  const [open, setOpen] = useState(false);
  const [openKeepDialog, setOpenKeepDialog] = useState(false);
  const [selectedModuleName, setSelectedModuleName] = useState('');
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  // 이름 검색 + 동명이인 선택
  const [nameQuery, setNameQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = () => dispatch(logout());

  const handleRefreshExpired = () => {
    navigate('/adminLogin');
  };

  const http = new Http({ onRefreshExpired: handleRefreshExpired, onLogout: handleLogout });

  const fetchUserData = useCallback(async (studentNumberParam) => {
    const numberToUse = studentNumberParam ?? studentNumber;
    if (!numberToUse) return;

    try {
      const urls = [
        `/api/admin/students/${numberToUse}/certificates`,
        `/api/admin/students/${numberToUse}/scholarships`,
        `/api/admin/students/${numberToUse}/course-enrollments`,
        `/api/admin/students/${numberToUse}`,
        `/api/admin/students/${numberToUse}/module-status`
      ];


      const [certificatesRawData, scholarshipsRawData, courseEnrollmentsRawData, studentRawData, moduleStatusRawData] =
        await Promise.all(urls.map((url) => http.getWithToken(url)));

      const certificatesData = certificatesRawData.data.result;
      const scholarshipsData = scholarshipsRawData.data.result;
      const courseEnrollmentsData = courseEnrollmentsRawData.data.result;
      const studentData = studentRawData.data.result;
      const moduleStatusData = moduleStatusRawData.data.result;

      // 교과목 정보 생성
      const subjectInfoList = getSubjectInfoList(courseEnrollmentsData);

      const { issuedModules, applyingModules } = splitModulesData(certificatesData);
      const { issuedScholarships, applyingScholarships } = splitScholarshipsData(scholarshipsData);


      setStudentInfo((prev) => ({
        ...prev,
        studentDetail: studentData,
        issuedModules: issuedModules,
        applyingModules: applyingModules,
        issuedScholarships: issuedScholarships,
        applyingScholarships: applyingScholarships,
        subjectInfoList: subjectInfoList,
        courseEnrollmentsRaw: courseEnrollmentsData || [],
        selectionRequired: moduleStatusData?.selectionRequired || [],
        moduleStatusList: moduleStatusData?.modules || []
      }));

    } catch (error) {
      console.error('에러 발생:', error);
      alert('학생 정보를 불러오는데 실패했습니다. 학번을 다시 확인해주세요.');
    }
  }, [studentNumber]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const studentNumberFromUrl = searchParams.get('studentNumber');
    if (studentNumberFromUrl && studentNumberFromUrl.length === 10) {
      setStudentNumber(studentNumberFromUrl);
      fetchUserData(studentNumberFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleInputChange = (e) => {
    const input = e.target.value;
    setStudentNumber(input);
  };

  const handleCheckClick = () => {
    if (!studentNumber || studentNumber.length !== 10) {
      alert('학번을 10자리 숫자로 입력해주세요.');
      return;
    }
    fetchUserData();
  };

  // 이름으로 검색 → 동명이인이면 선택 다이얼로그, 1명이면 바로 조회
  const handleNameSearch = async () => {
    const query = nameQuery.trim();
    if (!query) {
      alert('검색할 이름을 입력해주세요.');
      return;
    }
    try {
      const response = await http.getWithToken(`/api/admin/students/search?name=${encodeURIComponent(query)}`);
      const results = response.data.result || [];
      if (results.length === 0) {
        alert('검색 결과가 없습니다.');
        return;
      }
      if (results.length === 1) {
        handleSelectStudent(results[0].studentNumber);
        return;
      }
      setSearchResults(results);
      setSearchDialogOpen(true);
    } catch (error) {
      console.error('이름 검색 실패:', error);
      alert('학생 검색에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  // 검색 결과에서 학생 선택 → 해당 학번으로 상세 조회
  const handleSelectStudent = (selectedStudentNumber) => {
    setStudentNumber(selectedStudentNumber);
    setSearchDialogOpen(false);
    setSearchResults([]);
    fetchUserData(selectedStudentNumber);
  };

  const moduleNameList = useMemo(() => {
    if (!studentInfo.courseEnrollmentsRaw) return [];
    const names = new Set();
    studentInfo.courseEnrollmentsRaw.forEach((course) => {
      (course.moduleName || []).forEach((name) => names.add(name));
    });
    return [...names].sort();
  }, [studentInfo.courseEnrollmentsRaw]);

  const filteredCourses = useMemo(() => {
    if (!selectedModuleName || !studentInfo.courseEnrollmentsRaw) return [];
    return studentInfo.courseEnrollmentsRaw.filter((course) =>
      (course.moduleName || []).includes(selectedModuleName)
    );
  }, [selectedModuleName, studentInfo.courseEnrollmentsRaw]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleKeepOpen = () => setOpenKeepDialog(true);
  const handleKeepClose = () => {
    setOpenKeepDialog(false);
    setSelectedModuleName('');
    setSelectedCourseIds([]);
  };

  // 이수증 PDF 다운로드
  const handleDownloadCertificatePdf = async (moduleName) => {
    try {
      const response = await http.postWithToken(
        '/api/admin/certificates/pdf/student-module',
        { studentNumber: studentNumber, moduleName },
        { responseType: 'blob' }
      );
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '이수증 다운로드', 'height=1000, width=800');
    } catch (error) {
      alert('이수증 PDF 다운로드에 실패하였습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  // 장학증서 PDF 다운로드
  const handleDownloadScholarshipPdf = async (item) => {
    try {
      const response = await http.postWithToken(
        `/api/admin/scholarships/${item.id}/pdf`,
        {},
        { responseType: 'blob' }
      );
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '장학증서 다운로드', 'height=1000, width=800');
    } catch (error) {
      alert('장학증서 PDF 다운로드에 실패하였습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  const handleCourseToggle = (courseId) => {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId]
    );
  };

  const handleKeepSubmit = async () => {
    if (!selectedModuleName || selectedCourseIds.length === 0) {
      alert('모듈명을 선택하고 교과목을 하나 이상 선택해주세요.');
      return;
    }

    try {
      const requestBody = {
        studentNumber: studentNumber,
        moduleName: selectedModuleName,
        courseIds: selectedCourseIds
      };

      const response = await http.postWithToken('/api/admin/certificates/keep', requestBody);

      if (response.data.status === 'SUCCESS') {
        alert('처리가 완료되었습니다.');
        handleKeepClose();
      } else {
        alert('처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('API 호출 중 오류 발생:', error);
      alert('처리 중 오류가 발생했습니다.');
    }
  };

  // 활용 가능 교과목 표 (교과목 × 모듈 단위 행)
  const renderSubjectTable = (list) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>수강학기</TableCell>
            <TableCell>이수구분</TableCell>
            <TableCell>과정명</TableCell>
            <TableCell>모듈명</TableCell>
            <TableCell>표준교과목명</TableCell>
            <TableCell>학점</TableCell>
            <TableCell>교과목명</TableCell>
            <TableCell>지능형로봇학과개설여부</TableCell>
            <TableCell>활용가능여부</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                현재 목록이 비어있습니다.
              </TableCell>
            </TableRow>
          ) : (
            list.map((subject, index) => (
              <TableRow key={index}>
                <TableCell>{subject.semester}</TableCell>
                <TableCell>{subject.completionType}</TableCell>
                <TableCell>{subject.courseLevel}</TableCell>
                <TableCell>{subject.moduleName}</TableCell>
                <TableCell>{subject.standardCourseName}</TableCell>
                <TableCell>{subject.credit}</TableCell>
                <TableCell>{subject.courseName}</TableCell>
                {/* 지능형로봇학과개설여부: 백엔드가 코드표/설정 기반으로 판정 */}
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ color: subject.isOpenedByTargetDepartment ? 'success.main' : 'error.main', fontWeight: 'medium' }}
                  >
                    {subject.isOpenedByTargetDepartment ? 'O' : 'X'}
                  </Typography>
                </TableCell>
                {/* 활용가능여부: 이미 다른 이수증에 사용되었거나 인정기간을 벗어나면 '불가능' */}
                <TableCell>
                  <Typography variant="body2" sx={{ color: subject.isAvailable ? 'success.main' : 'error.main', fontWeight: 'medium' }}>
                    {subject.isAvailable ? '가능' : '불가능'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container sx={{ width: '100%' }}>
      <Box sx={{ marginBottom: '3rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="학번으로 조회하기"
            inputProps={{ 'aria-label': 'search' }}
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === 'Enter' && handleCheckClick()}
            value={studentNumber}
          />
        </Search>
        <Button variant="contained" onClick={handleCheckClick}>
          조회
        </Button>

        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="이름으로 검색하기"
            inputProps={{ 'aria-label': 'search-by-name' }}
            onChange={(e) => setNameQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleNameSearch()}
            value={nameQuery}
          />
        </Search>
        <Button variant="outlined" onClick={handleNameSearch}>
          이름 검색
        </Button>
      </Box>

      {studentInfo.studentDetail ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, marginBottom: 2 }}>
            <Typography variant="h1">{studentInfo.studentDetail.name}님의 이수 현황</Typography>
            <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
              <Button variant="contained" color="primary" size="medium" onClick={handleOpen}>
                학생 계정 설정
              </Button>
              <Button variant="contained" color="secondary" size="medium" onClick={handleKeepOpen}>
                기존 이수 과목 등록
              </Button>
              <AdminEditStudentInfo open={open} onClose={handleClose} studentId={studentNumber} />
            </Box>
          </Box>
          <Divider />

          <Card sx={{ marginY: 3 }}>
            <CardHeader title="학생 정보" />
            <CardContent>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 2
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">이름</Typography>
                  <Typography variant="body1">{studentInfo.studentDetail.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">단과대</Typography>
                  <Typography variant="body1">{studentInfo.studentDetail.collage ?? '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">소속학과</Typography>
                  <Typography variant="body1">{studentInfo.studentDetail.departmentName ?? '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">이메일</Typography>
                  <Typography variant="body1">{studentInfo.studentDetail.email ?? '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">휴대번호</Typography>
                  <Typography variant="body1">{studentInfo.studentDetail.phoneNumber ?? '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">현재 이수학기수</Typography>
                  <Typography variant="body1">
                    {studentInfo.studentDetail.completedSemesters != null
                      ? `${studentInfo.studentDetail.completedSemesters}학기`
                      : '-'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4, flexWrap: 'wrap', marginY: 5 }}>
            <Card sx={{ minWidth: 275 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <CardHeader title="발급 받은 이수증" />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 'bolder', color: 'primary.main' }}
                >{`${studentInfo.issuedModules.length}건`}</Typography>
              </Box>
              <CardContent>
                <List>
                  {studentInfo.issuedModules.length === 0 ? (
                    <ListItemButton>
                      <ListItemText primary={'현재 목록이 비어있습니다.'} />
                    </ListItemButton>
                  ) : (
                    studentInfo.issuedModules.map((module) => (
                      <ListItemButton key={module} sx={{ display: 'flex', alignItems: 'center' }}>
                        <ListItemText primary={module} />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadCertificatePdf(module);
                          }}
                        >
                          PDF
                        </Button>
                      </ListItemButton>
                    ))
                  )}
                </List>
              </CardContent>
            </Card>

            <Card sx={{ minWidth: 275 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <CardHeader title="신청 중인 이수증" />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 'bolder', color: 'warning.main' }}
                >{`${studentInfo.applyingModules.length}건`}</Typography>
              </Box>
              <CardContent>
                <List>
                  {studentInfo.applyingModules.length === 0 ? (
                    <ListItemButton>
                      <ListItemText primary={'현재 목록이 비어있습니다.'} />
                    </ListItemButton>
                  ) : (
                    studentInfo.applyingModules.map((module) => (
                      <ListItemButton key={module}>
                        <ListItemText primary={module} />
                      </ListItemButton>
                    ))
                  )}
                </List>
              </CardContent>
            </Card>

            <Card sx={{ minWidth: 275 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <CardHeader title="발급 받은 장학증서" />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 'bolder', color: 'primary.main' }}
                >{`${studentInfo.issuedScholarships.length}건`}</Typography>
              </Box>
              <CardContent>
                <List>
                  {studentInfo.issuedScholarships.length === 0 ? (
                    <ListItemButton>
                      <ListItemText primary={'현재 목록이 비어있습니다.'} />
                    </ListItemButton>
                  ) : (
                    studentInfo.issuedScholarships.map((item) => (
                      <ListItemButton key={item.id} sx={{ display: 'flex', alignItems: 'center' }}>
                        <ListItemText primary={item.moduleName} />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadScholarshipPdf(item);
                          }}
                        >
                          PDF
                        </Button>
                      </ListItemButton>
                    ))
                  )}
                </List>
              </CardContent>
            </Card>

            <Card sx={{ minWidth: 275 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <CardHeader title="신청 중인 장학증서" />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 'bolder', color: 'warning.main' }}
                >{`${studentInfo.applyingScholarships.length}건`}</Typography>
              </Box>
              <CardContent>
                <List>
                  {studentInfo.applyingScholarships.length === 0 ? (
                    <ListItemButton>
                      <ListItemText primary={'현재 목록이 비어있습니다.'} />
                    </ListItemButton>
                  ) : (
                    studentInfo.applyingScholarships.map((scholarship) => (
                      <ListItemButton key={scholarship}>
                        <ListItemText primary={scholarship} />
                      </ListItemButton>
                    ))
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ marginY: 5 }}>
            <Card>
              <CardHeader title="전체 교과목 이수현황" />
              <CardContent>{renderSubjectTable(studentInfo.subjectInfoList)}</CardContent>
            </Card>
          </Box>

          {/* 택1 필요 교과목 */}
          <Box sx={{ marginY: 5 }}>
            <Card>
              <CardHeader title="택1 필요 교과목 (여러 교과목 중 하나만 인정)" />
              <CardContent>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>표준교과목명</TableCell>
                        <TableCell>택1 필요 여부</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {studentInfo.selectionRequired.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} align="center">
                            현재 목록이 비어있습니다.
                          </TableCell>
                        </TableRow>
                      ) : (
                        studentInfo.selectionRequired.map((item) => (
                          <TableRow key={item.standardCourseName}>
                            <TableCell>{item.standardCourseName}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ color: item.required ? 'warning.main' : 'text.secondary', fontWeight: 'medium' }}
                              >
                                {item.required ? '택1 필요' : '해당없음'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>

          {/* 모듈·전공 이수 판정 */}
          <Box sx={{ marginY: 5 }}>
            <Card>
              <CardHeader title="모듈·전공 이수 판정" />
              <CardContent>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>과정</TableCell>
                        <TableCell>모듈명</TableCell>
                        <TableCell>이수학점</TableCell>
                        <TableCell>지능형로봇학과 학점</TableCell>
                        <TableCell>모듈이수</TableCell>
                        <TableCell>전공이수</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {studentInfo.moduleStatusList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            현재 목록이 비어있습니다.
                          </TableCell>
                        </TableRow>
                      ) : (
                        studentInfo.moduleStatusList.map((m) => (
                          <TableRow key={`${m.level}-${m.moduleName}`}>
                            <TableCell>{m.level}</TableCell>
                            <TableCell>{m.moduleName}</TableCell>
                            <TableCell>{m.earnedCredit}</TableCell>
                            <TableCell>{m.intelligentRoboticsCredit}</TableCell>
                            <TableCell>
                              {studentInfo.issuedModules.includes(m.moduleName) ? (
                                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                                  이수완료
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ color: m.moduleAvailable ? 'success.main' : 'error.main', fontWeight: 'medium' }}
                                >
                                  {m.moduleAvailable ? '가능' : '불가능'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ color: m.majorAvailable ? 'success.main' : 'error.main', fontWeight: 'medium' }}
                              >
                                {m.majorAvailable ? '가능' : '불가능'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, marginBottom: 2 }}>
          <Typography variant="h1">학번으로 학생을 조회해주세요</Typography>
        </Box>
      )}

      <Dialog open={openKeepDialog} onClose={handleKeepClose} maxWidth="sm" fullWidth>
        <DialogTitle>교과목 이수 현황 등록</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>모듈명</InputLabel>
              <Select
                value={selectedModuleName}
                label="모듈명"
                onChange={(e) => {
                  setSelectedModuleName(e.target.value);
                  setSelectedCourseIds([]);
                }}
              >
                {moduleNameList.map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedModuleName && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  교과목 선택 ({selectedCourseIds.length}개 선택됨)
                </Typography>
                {filteredCourses.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    해당 모듈에 이수한 교과목이 없습니다.
                  </Typography>
                ) : (
                  <FormGroup>
                    {filteredCourses.map((course) => (
                      <FormControlLabel
                        key={course.courseId}
                        control={
                          <Checkbox
                            checked={selectedCourseIds.includes(course.courseId)}
                            onChange={() => handleCourseToggle(course.courseId)}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ color: course.isAvailable ? 'text.primary' : 'text.disabled' }}
                          >
                            {course.courseName}
                            {!course.isAvailable && ' (사용불가)'}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepClose}>취소</Button>
          <Button
            onClick={handleKeepSubmit}
            variant="contained"
            color="primary"
            disabled={!selectedModuleName || selectedCourseIds.length === 0}
          >
            등록
          </Button>
        </DialogActions>
      </Dialog>

      {/* 이름 검색 결과 선택 다이얼로그 (동명이인 대비) */}
      <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>학생 선택 ({searchResults.length}명)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            동명이인이 있을 수 있습니다. 조회할 학생을 선택하세요.
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>이름</TableCell>
                  <TableCell>학번</TableCell>
                  <TableCell>소속학과</TableCell>
                  <TableCell>단과대</TableCell>
                  <TableCell align="right">선택</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchResults.map((student) => (
                  <TableRow key={student.studentNumber} hover>
                    <TableCell>{student.name}</TableCell>
                    <TableCell>{student.studentNumber}</TableCell>
                    <TableCell>{student.departmentName ?? '-'}</TableCell>
                    <TableCell>{student.collage ?? '-'}</TableCell>
                    <TableCell align="right">
                      <Button variant="contained" size="small" onClick={() => handleSelectStudent(student.studentNumber)}>
                        선택
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminStudentInfo;
